import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Modal } from 'semantic-ui-react';

import styles from './CameraCapture.module.scss';

const CameraCapture = React.memo(({ onCapture, multiple, fieldLabel }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [lastShot, setLastShot] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Iniciar cámara
  const startCamera = async () => {
    setIsReviewing(false);
    setLastShot(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Preferir cámara trasera
        audio: false,
      });

      setStream(mediaStream);
      setShowCamera(true);

      // Esperar a que el video esté listo
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('No se pudo acceder a la cámara. Asegúrate de dar permisos.');
    }
  };

  // Detener cámara
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setIsReviewing(false);
    setLastShot(null);
  };

  // Tomar la foto (congelar y mostrar para revisión)
  const takeShot = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setLastShot(dataUrl);
    setIsReviewing(true);
  };

  // Reintentar (volver a la cámara en vivo)
  const handleRetake = () => {
    setLastShot(null);
    setIsReviewing(false);
  };

  // Usar esta foto
  const handleUsePhoto = () => {
    if (!lastShot) return;

    // Siempre permitir múltiples fotos: agregar a la lista y seguir tomando fotos
    setCapturedPhotos(prev => [...prev, lastShot]);
    setIsReviewing(false);
    setLastShot(null);
  };

  // Quitar una foto de la lista de capturadas
  const removePhoto = index => {
    setCapturedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Confirmar fotos
  const handleDone = () => {
    if (onCapture && capturedPhotos.length > 0) {
      onCapture(capturedPhotos);
      setCapturedPhotos([]); // Limpiar para la próxima vez que se abra la cámara
    }
    stopCamera();
  };

  // Efecto para asegurar que el video se conecte al stream cuando NO estamos revisando
  useEffect(() => {
    if (showCamera && !isReviewing && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [showCamera, isReviewing, stream]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return (
    <>
      <div className={styles.cameraButton}>
        <Button type="button" basic icon labelPosition="left" onClick={startCamera}>
          <Icon name="camera" />
          {capturedPhotos.length > 0 ? 'Add more photos' : 'Open Camera'}
        </Button>
        {capturedPhotos.length > 0 && (
          <span className={styles.photoCount}>
            {capturedPhotos.length} {capturedPhotos.length === 1 ? 'photo' : 'photos'} captured
          </span>
        )}
      </div>

      {/* Modal de cámara */}
      {showCamera && (
        <Modal open onClose={stopCamera} size="large" closeIcon>
          <Modal.Header className={styles.cameraHeader}>
            <Icon name="camera" />
            {fieldLabel || 'Take Photo'}
          </Modal.Header>
          <Modal.Content className={styles.cameraContent}>
            <div className={styles.videoContainer}>
              {!isReviewing ? (
                <video ref={videoRef} autoPlay playsInline muted className={styles.video} />
              ) : (
                <img src={lastShot} alt="Last shot" className={styles.reviewImage} />
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>

            {/* Lista de fotos ya capturadas en esta sesión */}
            {capturedPhotos.length > 0 && (
              <div className={styles.capturedPreview}>
                <p>
                  <Icon name="check circle" />
                  <strong>{capturedPhotos.length} photo(s) ready to add</strong>
                </p>
                <div className={styles.thumbnailGrid}>
                  {capturedPhotos.map((photo, idx) => (
                    <div key={idx} className={styles.thumbnail}>
                      <img src={photo} alt={`Captured ${idx + 1}`} />
                      <Button
                        circular
                        icon="close"
                        size="mini"
                        color="red"
                        className={styles.removeBtn}
                        onClick={() => removePhoto(idx)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions className={styles.modalActions}>
            {!isReviewing ? (
              <div className={styles.actionButtons}>
                <Button type="button" onClick={stopCamera} className={styles.mobileFluid}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  color="green"
                  icon
                  labelPosition="left"
                  onClick={takeShot}
                  className={styles.mobileFluid}
                >
                  <Icon name="circle" />
                  Capture
                </Button>
              </div>
            ) : (
              <div className={styles.actionButtons}>
                <Button
                  type="button"
                  color="orange"
                  icon
                  labelPosition="left"
                  onClick={handleRetake}
                  className={styles.mobileFluid}
                >
                  <Icon name="refresh" />
                  Retake
                </Button>
                <Button
                  type="button"
                  color="green"
                  icon
                  labelPosition="left"
                  onClick={handleUsePhoto}
                  className={styles.mobileFluid}
                >
                  <Icon name="plus" />
                  Add Photo
                </Button>
              </div>
            )}

            {capturedPhotos.length > 0 && !isReviewing && (
              <Button
                type="button"
                color="blue"
                icon
                labelPosition="left"
                onClick={handleDone}
                className={styles.finishBtn}
              >
                <Icon name="save" />
                Finish ({capturedPhotos.length})
              </Button>
            )}
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
});

CameraCapture.propTypes = {
  onCapture: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  fieldLabel: PropTypes.string,
};

CameraCapture.defaultProps = {
  multiple: false,
  fieldLabel: '',
};

export default CameraCapture;
